import React, { useState, useEffect, useCallback } from 'react';
import { AddonSelector } from './components/AddonSelector';
import { fetchApplicableAddons } from './utils/apiClient';
import type { ProductAddon } from './types';

interface AppProps {
  apiUrl: string;
  productId: string;
  initialVariantId: string;
}

export const App: React.FC<AppProps> = ({ apiUrl, productId, initialVariantId }) => {
  const [addons, setAddons] = useState<ProductAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentVariantId, setCurrentVariantId] = useState<string>(initialVariantId);

  // Function to extract variant ID from URL
  const getVariantIdFromUrl = useCallback((): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  }, []);

  // Function to get current variant ID (from URL or fallback to initial)
  const getCurrentVariantId = useCallback((): string => {
    const urlVariantId = getVariantIdFromUrl();
    return urlVariantId || initialVariantId;
  }, [initialVariantId, getVariantIdFromUrl]);

  // Load addons for current variant
  const loadAddons = useCallback(async (variantId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchApplicableAddons(apiUrl, productId, variantId);
      setAddons(response.data);
    } catch (err: any) {
      console.error('Error loading addons:', err);
      setError(err.message || 'Failed to load addon data');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, productId]);

  // Initial load and monitor URL changes
  useEffect(() => {
    let lastVariantId = getCurrentVariantId();
    setCurrentVariantId(lastVariantId);

    // Initial load
    if (productId && lastVariantId) {
      loadAddons(lastVariantId);
    }

    const handleUrlChange = () => {
      const newVariantId = getCurrentVariantId();
      if (newVariantId !== lastVariantId) {
        lastVariantId = newVariantId;
        setCurrentVariantId(newVariantId);
        if (productId && newVariantId) {
          loadAddons(newVariantId);
        }
      }
    };

    // Listen for popstate (back/forward button)
    window.addEventListener('popstate', handleUrlChange);

    // Monitor URL changes via polling (for SPA navigation and direct URL changes)
    const checkInterval = setInterval(() => {
      handleUrlChange();
    }, 500); // Check every 500ms

    // Also listen for hashchange
    window.addEventListener('hashchange', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
      clearInterval(checkInterval);
    };
  }, [apiUrl, productId, initialVariantId, getCurrentVariantId, loadAddons]);

  if (loading) {
    return (
      <div className="intan-addon-loading" style={{ padding: '20px', textAlign: 'center' }}>
        <div className="intan-spinner" style={{
          width: '32px',
          height: '32px',
          border: '3px solid #f3f4f6',
          borderTop: '3px solid #6f1920',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto',
        }}></div>
      </div>
    );
  }


  if (error) {
    return (
      <div className="intan-addon-error" style={{ padding: '20px' }}>
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
          <p className="font-semibold">Error</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Always render AddonSelector if product is qualified (data attribute checked during init)
  // This ensures the buy button replacement is always shown, even if no addons are available
  return (
    <AddonSelector
      addons={addons}
      mainProductId={productId}
      mainVariantId={currentVariantId}
      apiUrl={apiUrl}
    />
  );
};

